import { format as formatDateFns } from "date-fns";
import { toZonedTime, format as formatTz } from "date-fns-tz";

// Sydney timezone - ALL times in the app will use this
export const TIMEZONE = "Australia/Sydney";

/**
 * Convert any date to Sydney timezone
 * @param {Date|string} date - Date object or ISO string
 * @returns {Date} - Date in Sydney timezone
 */
export function toSydneyTime(date) {
  if (!date) return null;
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return toZonedTime(dateObj, TIMEZONE);
}

/**
 * Format a date in Sydney timezone
 * @param {Date|string} date - Date object or ISO string
 * @param {string} formatStr - date-fns format string
 * @returns {string} - Formatted date string in Sydney time
 */
export function formatSydney(date, formatStr = "dd MMM yyyy, h:mm a") {
  if (!date) return "-";
  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    const sydneyDate = toZonedTime(dateObj, TIMEZONE);
    return formatTz(sydneyDate, formatStr, { timeZone: TIMEZONE });
  } catch (e) {
    console.error("Error formatting date:", e);
    return "-";
  }
}

/**
 * Get today's date string in Sydney timezone (YYYY-MM-DD)
 * @returns {string} - Today's date in Sydney
 */
export function getTodaySydney() {
  const now = new Date();
  const sydneyNow = toZonedTime(now, TIMEZONE);
  return formatDateFns(sydneyNow, "yyyy-MM-dd");
}

/**
 * Format time only in Sydney timezone
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} - Formatted time string (e.g., "10:30 AM")
 */
export function formatTimeSydney(date) {
  return formatSydney(date, "h:mm a");
}

/**
 * Format date only in Sydney timezone
 * @param {Date|string} date - Date object or ISO string  
 * @returns {string} - Formatted date string (e.g., "12 Feb 2026")
 */
export function formatDateSydney(date) {
  return formatSydney(date, "dd MMM yyyy");
}

/**
 * Format full date with day name in Sydney timezone
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} - Formatted date string (e.g., "Thursday, February 12, 2026")
 */
export function formatFullDateSydney(date) {
  return formatSydney(date, "EEEE, MMMM d, yyyy");
}

/**
 * Format date for tables (shorter format)
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} - Formatted date string (e.g., "Thu, Feb 12, 2026")
 */
export function formatTableDateSydney(date) {
  return formatSydney(date, "EEE, MMM dd, yyyy");
}

/**
 * Get current Sydney date/time display
 * @returns {string} - Current Sydney time (e.g., "12 Feb 2026, 10:30 AM AEDT")
 */
export function getCurrentSydneyDisplay() {
  const now = new Date();
  return formatSydney(now, "dd MMM yyyy, h:mm a zzz");
}
